// Winter storage contracts: the per-boat Contract Builder, the printable contract, the
// Storage Contracts list (Work Orders › Storage Contracts) and Review & create WOs.
// Split out of app.js (2026-10-03). The app.js values and helpers it uses come in through
// setStorageContractDeps() (see estimates.js for why it doesn't import app.js back).
// Pricing tiers/settings loading and the work-order form's storage toggle stay in app.js.
import { fmtEstDocDate } from './estimates';
import { invoiceStorageContract, contractIndexKey, openInvoiceEditor } from './invoices';
import { usesDockmaster } from './marinaConfig';
import { ref, get } from 'firebase/database';
import { database, functions } from './initFirebase';
import { httpsCallable } from 'firebase/functions';
import { openSignLog } from './signLog';
import { priceStorageContract, storageSettings, contractServiceApplies, contractServicePrice } from './storageContractCore';
import { openCompose, pagesToPdf } from './mailClient';
import { openEmailHistory } from './emailHistory';
import { STORAGE_SERVICE_CATALOG, currentSeasonLabel, escapeHTML, fetchIdToken, getDB, idbCursorAll, toastMessage } from './utilities';

let shell = null;
export function setStorageContractDeps(deps) { shell = deps; }

// ============================================================
// H1 — Storage Contract Builder (Phase 3)
// ============================================================

// Unsaved UI state for whichever boat's builder is currently open — reset
// each time the builder opens. The persisted record is
// Boat.Storage_Contract, written only by "Save draft" below.
let contractBuilderState = null;

function contractMoney(n) {
  return '$' + Number(n || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
}

function openStorageContractBuilderModal() {
  const modal = document.getElementById('storageContractBuilder');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.style.display = 'flex';
}

function closeStorageContractBuilderModal() {
  const modal = document.getElementById('storageContractBuilder');
  if (!modal) return;
  modal.style.display = 'none';
  modal.classList.add('hidden');
}

document.getElementById('open-storage-contract-builder-btn')?.addEventListener('click', async () => {
  const customerId = document.getElementById('work-order-form-customer-id').value;
  const boatId = document.getElementById('work-order-form-boat-id').value;
  if (!customerId || !boatId) {
    toastMessage('Select a customer and boat first', 'red');
    return;
  }
  await openStorageContractBuilder(customerId, boatId);
});

document.getElementById('contractBuilderClose')?.addEventListener('click', closeStorageContractBuilderModal);
document.getElementById('contractBuilderCancelBtn')?.addEventListener('click', closeStorageContractBuilderModal);
document.getElementById('storageContractBuilder')?.addEventListener('click', (e) => {
  if (e.target.id === 'storageContractBuilder') closeStorageContractBuilderModal();
});

async function openStorageContractBuilder(customerId, boatId) {
  const customer = await shell.getCustomerRecord(customerId);
  const boat = customer?.Boats?.[boatId];
  if (!customer || !boat) {
    toastMessage('Could not load that customer/boat record', 'red');
    return;
  }

  const existing = boat.Storage_Contract || null;

  // Falls back to the boat's own Haul_Date/Launch_Date (set via the WO
  // form's date fields, addHaulLaunchStorageDates()) when the contract
  // hasn't been saved with its own haul/launch week yet — otherwise a date
  // entered right before opening the builder silently didn't carry over.
  contractBuilderState = {
    customerId,
    boatId,
    customer,
    boat,
    haulWeek: existing?.haulWeek || boat.Haul_Date || '',
    launchWeek: existing?.launchWeek || boat.Launch_Date || '',
    // 'obmc' is the old value for the marina doing it (contracts saved before the rename).
    zincChoice: existing?.zincChoice === 'obmc' ? 'marina' : (existing?.zincChoice || ''),
    additionalRequests: existing?.additionalRequests || '',
    selections: { ...(existing?.selections || {}) }, // keyed by service name
    status: existing?.status || 'draft',
    totals: existing?.totals || {},
  };

  document.getElementById('contractBuilderBreadcrumbLabel').textContent = `Winter Storage Agreement — ${boat.Boat_Name || 'Boat'}`;
  const statusPill = document.getElementById('contractBuilderStatusPill');
  if (statusPill) {
    statusPill.textContent = contractBuilderState.status.charAt(0).toUpperCase() + contractBuilderState.status.slice(1);
    statusPill.className = 'wo-status-pill wo-status-pill-' + contractBuilderState.status;
  }

  document.getElementById('contractBuilderHaulWeek').value = contractBuilderState.haulWeek;
  document.getElementById('contractBuilderLaunchWeek').value = contractBuilderState.launchWeek;
  document.getElementById('contractBuilderRequests').value = contractBuilderState.additionalRequests;
  document.querySelectorAll('input[name="contractBuilderZinc"]').forEach(r => { r.checked = (r.value === contractBuilderState.zincChoice); });

  renderContractBuilderOwnerGrid(customer, boat);
  renderContractBuilderServices();
  updateContractBuilderTotals();

  openStorageContractBuilderModal();
}

function renderContractBuilderOwnerGrid(customer, boat) {
  const grid = document.getElementById('contractBuilderOwnerGrid');
  if (!grid) return;
  grid.innerHTML = '';

  const customerName = customer.Last_Name && customer.First_Name
    ? `${customer.Last_Name}, ${customer.First_Name}`
    : (customer.Last_Name || customer.First_Name || '');

  const fields = [
    { label: "Owner's name", value: customerName },
    { label: 'Phone', value: shell.formatPhoneNumber(customer.Phone) },
    { label: 'Mooring / Slip #', value: boat.Mooring_Number || '' },
    { label: 'Address', value: [customer.Street, customer.City, customer.State, customer.Zip].filter(Boolean).join(', '), wide: true },
    { label: 'Email', value: customer.Email || '' },
    { label: 'Boat name', value: boat.Boat_Name || '' },
    { label: 'Length & beam', value: (boat.Boat_Length && boat.Boat_Beam) ? `${boat.Boat_Length}' × ${boat.Boat_Beam}'` : '' },
    { label: 'Make / Model', value: [boat.Boat_Make, boat.Boat_Model].filter(Boolean).join(' ') },
    { label: 'Year · Type', value: [boat.Boat_Year, boat.Boat_Type].filter(Boolean).join(' · ') },
    { label: 'Key / Combo', value: boat.Combo_Key || '' },
    { label: 'Insurance carrier', value: boat.Insurance_Company || '', missingText: 'not on file' },
    { label: 'Policy number', value: boat.Insurance_Policy || '', missingText: 'not on file' },
  ];

  fields.forEach(f => {
    const wrap = document.createElement('div');
    wrap.className = 'contract-builder-owner-field' + (f.wide ? ' contract-builder-owner-field-wide' : '');
    const label = document.createElement('div');
    label.className = 'contract-builder-owner-field-label';
    label.textContent = f.label;
    const value = document.createElement('div');
    const isMissing = !f.value && f.missingText;
    value.className = 'contract-builder-owner-field-value' + (isMissing ? ' contract-builder-owner-field-missing' : '');
    value.textContent = f.value || (f.missingText || '—');
    wrap.appendChild(label);
    wrap.appendChild(value);
    grid.appendChild(wrap);
  });

  const pricedFor = document.getElementById('contractBuilderPricedFor');
  if (pricedFor) {
    const bits = [];
    if (boat.Boat_Length) bits.push(`${boat.Boat_Length}' ${(boat.Boat_Type || '').toLowerCase().includes('sail') ? 'sail' : 'power'}`);
    if (boat.Engine_Type) bits.push(boat.Engine_Type.toLowerCase());
    if (boat.Number_of_Batteries) bits.push(`${boat.Number_of_Batteries} batteries`);
    pricedFor.textContent = bits.length ? `priced for a ${bits.join(' · ')}` : '';
  }
}

// perFoot/flat services price against the boat on record; 'tm' (time &
// materials) has no fixed line price — excluded from the contract total.
// The pricing itself is storageContractCore.js, shared with the portal and server.
function contractBuilderServicePrice(entry, boatOverride) {
  return contractServicePrice(entry, boatOverride || contractBuilderState?.boat);
}

function renderContractBuilderServices() {
  const container = document.getElementById('contractBuilderServices');
  if (!container) return;
  container.innerHTML = '';

  let lastCategory = null;
  STORAGE_SERVICE_CATALOG.filter((entry) => contractServiceApplies(entry, contractBuilderState?.boat)).forEach((entry) => {
    if (entry.category !== lastCategory) {
      lastCategory = entry.category;
      const groupTitle = document.createElement('div');
      groupTitle.className = 'contract-builder-service-group-title';
      groupTitle.textContent = entry.category;
      container.appendChild(groupTitle);
    }

    const checked = !!contractBuilderState.selections[entry.service];
    const row = document.createElement('label');
    row.className = 'contract-builder-service-row' + (checked ? ' checked' : '');

    const checkbox = document.createElement('input');
    checkbox.type = 'checkbox';
    checkbox.checked = checked;

    const name = document.createElement('span');
    name.className = 'contract-builder-service-name';
    name.textContent = entry.service;

    const rate = document.createElement('span');
    rate.className = 'contract-builder-service-rate';
    rate.textContent = entry.rate;

    const priceEl = document.createElement('span');
    priceEl.className = 'contract-builder-service-price';
    priceEl.textContent = entry.unit === 'tm' ? 'T&M' : (checked ? contractMoney(contractBuilderServicePrice(entry)) : '—');

    checkbox.addEventListener('change', () => {
      contractBuilderState.selections[entry.service] = checkbox.checked;
      row.classList.toggle('checked', checkbox.checked);
      priceEl.textContent = entry.unit === 'tm' ? 'T&M' : (checkbox.checked ? contractMoney(contractBuilderServicePrice(entry)) : '—');
      updateContractBuilderTotals();
    });

    row.appendChild(checkbox);
    row.appendChild(name);
    row.appendChild(rate);
    row.appendChild(priceEl);
    container.appendChild(row);
  });
}

function updateContractBuilderTotals() {
  if (!contractBuilderState) return;
  const boat = contractBuilderState.boat;
  const length = Number(boat.Boat_Length) || 0;
  const beam = Number(boat.Boat_Beam) || 0;

  // Tax on storage only, the environmental fee on storage + services (storageContractCore.js).
  const p = priceStorageContract(boat, contractBuilderState.selections, storageSettings(shell.storageContractSettings));
  const { minCharge, taxPct, deposit, launchCredit, tier, unitFt, belowMin } = p;
  const { storage: storageSubtotal, services: servicesSubtotal, tax, envFee, total } = p.totals;

  const estimatorLine = document.getElementById('contractBuilderEstimatorLine');
  if (estimatorLine) {
    estimatorLine.textContent = tier
      ? `${length}' × ${beam}' = ${unitFt} unit ft × $${tier.rate.toFixed(2)} = ${contractMoney(unitFt * tier.rate)}`
      : (length ? `${length}' has no matching rate tier — check Settings > Storage rates` : 'Enter the boat\'s length & beam to estimate');
  }
  const estimatorHint = document.getElementById('contractBuilderEstimatorHint');
  if (estimatorHint) {
    estimatorHint.textContent = belowMin
      ? `Below the ${contractMoney(minCharge)} minimum — minimum charge applies.`
      : `${contractMoney(minCharge)} minimum storage charge · ${contractMoney(launchCredit)} credit if launch service purchased.`;
  }

  const totalLines = document.getElementById('contractBuilderTotalLines');
  if (totalLines) {
    totalLines.innerHTML = `
      <div class="contract-builder-total-line"><span>Winter storage</span><span>${contractMoney(storageSubtotal)}</span></div>
      <div class="contract-builder-total-line"><span>Sales tax · ${(taxPct * 100).toFixed(3).replace(/\.?0+$/, '')}%</span><span>${contractMoney(tax)}</span></div>
      <div class="contract-builder-total-line"><span>Selected services</span><span>${contractMoney(servicesSubtotal)}</span></div>
      <div class="contract-builder-total-line"><span>Environmental fee</span><span>${contractMoney(envFee)}</span></div>
      <div class="contract-builder-total-line contract-builder-total-line-grand"><span>Total</span><span>${contractMoney(total)}</span></div>
    `;
  }

  const paymentLines = document.getElementById('contractBuilderPaymentLines');
  if (paymentLines) {
    const balance = Math.max(total - deposit, 0);
    const half = balance / 2;
    paymentLines.innerHTML = `
      <div class="contract-builder-payment-line"><span>Due at signing</span><span>${contractMoney(deposit)}</span></div>
      <div class="contract-builder-payment-line"><span>Before haul week — 50% of balance</span><span>${contractMoney(half)}</span></div>
      <div class="contract-builder-payment-line"><span>Final balance</span><span>${contractMoney(balance - half)}</span></div>
    `;
  }

  contractBuilderState.totals = { storage: storageSubtotal, tax, services: servicesSubtotal, envFee, total };
}

// ============================================================
// Part 3 — Printable Storage Contract (Phase 5)
// Same populate-a-hidden-overlay-then-window.print() pattern as
// openEstimateDocument/openInvoiceDocument above; reuses their
// ESTIMATE_LETTERHEAD_ADDRESS/fmtEstDocDate rather than duplicating them.
// ============================================================

function contractDocRateTiers() {
  return (shell.storageContractSettings?.tiers?.length) ? shell.storageContractSettings.tiers : shell.STORAGE_PRICING_FALLBACK.tiers;
}

function openContractDocument() {
  const container = document.getElementById('contractDocContainer1');
  const state = contractBuilderState;
  if (!container || !state) return;

  const setText = (id, text) => { const el = document.getElementById(id); if (el) el.textContent = text ?? ''; };
  const { customer, boat } = state;
  const length = Number(boat?.Boat_Length) || 0;
  const beam = Number(boat?.Boat_Beam) || 0;
  const settings = shell.storageContractSettings || {};
  // Tax on storage only, the environmental fee on storage + services (storageContractCore.js).
  const p = priceStorageContract(boat, state.selections, storageSettings(settings));
  const { envPct, taxPct, minCharge, deposit, launchCredit, tier, unitFt } = p;
  const { storage: storageSubtotal, services: servicesSubtotal, tax, envFee, total } = p.totals;

  const customerName = customer?.Last_Name && customer?.First_Name
    ? `${customer.Last_Name}, ${customer.First_Name}`
    : (customer?.Last_Name || customer?.First_Name || '');

  setText('contractDocSeason', `Season ${settings.season || currentSeasonLabel()}`);
  // Contract # is derived, not a separately-tracked sequence — stable per
  // boat+year so reprinting the same contract shows the same number.
  // Boat IDs are often Firebase push-ids (mostly letters) — slicing raw
  // characters (not digit-filtering) avoids an empty/short number for
  // boats whose id happens to carry few or no digits.
  setText('contractDocNumber', `SC-${new Date().getFullYear()}-${(state.boatId || '').toString().slice(-4).toUpperCase().padStart(4, '0')}`);

  const returnBy = settings.dates?.returnBy;
  setText('contractDocReturnNotice', returnBy
    ? `Please complete, sign, and return this agreement on or before ${fmtEstDocDate(new Date(returnBy + 'T00:00:00'))} to reserve your requested haul and launch weeks. Weeks are assigned first come, first served once your completed contract and deposit are received.`
    : 'Please complete, sign, and return this agreement to reserve your requested haul and launch weeks. Weeks are assigned first come, first served once your completed contract and deposit are received.');

  const ownerFields = [
    { label: "Owner's name", value: customerName },
    { label: 'Address', value: [customer?.Street, customer?.City, customer?.State, customer?.Zip].filter(Boolean).join(', '), wide: true },
    { label: 'Phone', value: shell.formatPhoneNumber(customer?.Phone) },
    { label: 'Email', value: customer?.Email || '' },
    { label: 'Boat name', value: boat?.Boat_Name || '' },
    { label: 'Length & beam', value: (length && beam) ? `${length}' × ${beam}'` : '' },
    { label: 'Manufacturer / Model', value: [boat?.Boat_Make, boat?.Boat_Model].filter(Boolean).join(' ') },
    { label: 'Year · Type', value: [boat?.Boat_Year, boat?.Boat_Type].filter(Boolean).join(' · ') },
    { label: 'Key or combo', value: boat?.Combo_Key || '' },
    { label: 'Insurance carrier', value: boat?.Insurance_Company || '' },
    { label: 'Policy number', value: boat?.Insurance_Policy || '' },
    { label: 'Mooring / slip #', value: boat?.Mooring_Number || '' },
  ];
  const ownerGrid = document.getElementById('contractDocOwnerGrid');
  if (ownerGrid) {
    ownerGrid.innerHTML = ownerFields.map(f => `
      <div class="contractDoc-fill-field${f.wide ? ' contractDoc-fill-field-wide' : ''}">
        <div class="contractDoc-fill-label">${escapeHTML(f.label)}</div>
        <div class="contractDoc-fill-value">${escapeHTML(f.value || '')}</div>
      </div>
    `).join('');
  }

  const weeksRow = document.getElementById('contractDocWeeksRow');
  if (weeksRow) {
    weeksRow.innerHTML = `
      <div><div class="contractDoc-fill-label">Haul week</div><div class="contractDoc-fill-value">${state.haulWeek ? fmtEstDocDate(new Date(state.haulWeek + 'T00:00:00')) : ''}</div></div>
      <div><div class="contractDoc-fill-label">Launch week</div><div class="contractDoc-fill-value">${state.launchWeek ? fmtEstDocDate(new Date(state.launchWeek + 'T00:00:00')) : ''}</div></div>
    `;
  }

  const tiers = contractDocRateTiers();
  const rateTable = document.getElementById('contractDocRateTable');
  if (rateTable) {
    rateTable.innerHTML = `
      <thead><tr><th>Boat length</th><th>Rate / unit ft</th></tr></thead>
      <tbody>
        ${tiers.map(t => `
          <tr class="${tier === t ? 'contractDoc-rate-tier-active' : ''}">
            <td>${t.from <= 0 ? `Up to ${t.to} ft` : `${t.from} – ${t.to} ft`}${tier === t ? ' · this boat' : ''}</td>
            <td>$${t.rate.toFixed(2)}</td>
          </tr>
        `).join('')}
      </tbody>
    `;
  }

  setText('contractDocEstimatorLine', tier ? `${length} × ${beam} = ${unitFt} unit ft × $${tier.rate.toFixed(2)}` : 'Enter boat length & beam to estimate');
  setText('contractDocEstimatorTotal', contractMoney(storageSubtotal));
  setText('contractDocEstimatorNote', `Minimum winter storage charge is ${contractMoney(minCharge)}. Deduct ${contractMoney(launchCredit)} if Launch Service was purchased this year. ${shell.cachedBusinessProfile?.name || 'The marina'} has final say on boat length and price totals.`);

  const servicesBody = document.getElementById('contractDocServicesBody');
  if (servicesBody) {
    let lastCategory = null;
    let html = '';
    STORAGE_SERVICE_CATALOG.filter(entry => contractServiceApplies(entry, boat)).forEach(entry => {
      const checked = !!state.selections[entry.service];
      if (entry.category !== lastCategory) {
        lastCategory = entry.category;
        html += `<div class="contractDoc-service-group-title">${escapeHTML(entry.category)}</div>`;
      }
      const price = entry.unit === 'tm' ? 'T&M' : (checked ? contractMoney(contractBuilderServicePrice(entry, boat)) : '—');
      html += `
        <div class="contractDoc-service-row${checked ? ' selected' : ''}">
          <span class="contractDoc-service-check">${checked ? '✓' : ''}</span>
          <span class="contractDoc-service-name">${escapeHTML(entry.service)}</span>
          <span class="contractDoc-service-rate">${escapeHTML(entry.rate)}</span>
          <span class="contractDoc-service-price">${price}</span>
        </div>
      `;
    });
    servicesBody.innerHTML = html;
  }

  setText('contractDocZincChoice', state.zincChoice === 'owner'
    ? 'Owner will change at least one week prior to launch date'
    : `${shell.cachedBusinessProfile?.name || 'Marina'} to complete — T&M`);

  const totalsStack = document.getElementById('contractDocTotalsStack');
  if (totalsStack) {
    totalsStack.innerHTML = `
      <div class="contractDoc-totals-row"><span>Winter storage · ${unitFt} unit ft</span><span>${contractMoney(storageSubtotal)}</span></div>
      <div class="contractDoc-totals-row"><span>Sales tax · ${(taxPct * 100).toFixed(3).replace(/\.?0+$/, '')}%</span><span>${contractMoney(tax)}</span></div>
      <div class="contractDoc-totals-row"><span>Selected services</span><span>${contractMoney(servicesSubtotal)}</span></div>
      <div class="contractDoc-totals-row"><span>Environmental fee · ${(envPct * 100).toFixed(1)}%</span><span>${contractMoney(envFee)}</span></div>
      <div class="contractDoc-totals-row contractDoc-totals-row-total"><span>Total</span><span>${contractMoney(total)}</span></div>
    `;
  }

  const balance = Math.max(total - deposit, 0);
  const half = balance / 2;
  const paymentStack = document.getElementById('contractDocPaymentStack');
  if (paymentStack) {
    paymentStack.innerHTML = `
      <div class="contractDoc-payment-row"><span>Due at signing — non-refundable deposit</span><span>${contractMoney(deposit)}</span></div>
      <div class="contractDoc-payment-row"><span>Before haul week — 50% of balance</span><span>${contractMoney(half)}</span></div>
      <div class="contractDoc-payment-row"><span>Final balance</span><span>${contractMoney(balance - half)}</span></div>
    `;
  }

  // {{business}} in the terms becomes the business name.
  setText('contractDocTermsText', (settings.termsText || '').replace(/\{\{business\}\}/g, shell.cachedBusinessProfile?.name || 'the marina'));

  setText('contractDocFooterAddress1', shell.ESTIMATE_LETTERHEAD_ADDRESS);
  setText('contractDocFooterAddress2', shell.ESTIMATE_LETTERHEAD_ADDRESS);
  setText('contractDocFooterAddress3', shell.ESTIMATE_LETTERHEAD_ADDRESS);
  setText('contractDocPage2Sub', `${boat?.Boat_Name || ''} · ${customerName}`);
  setText('contractDocPage3Sub', `${boat?.Boat_Name || ''} · ${customerName}`);

  container.classList.remove('hidden');
}

function closeContractDocument() {
  document.getElementById('contractDocContainer1')?.classList.add('hidden');
}

document.getElementById('contractDocCloseButton')?.addEventListener('click', closeContractDocument);
document.getElementById('contractDocPrintButton')?.addEventListener('click', () => window.print());

// Emails: what's been sent about this boat's storage contract (emailHistory.js).
document.getElementById('contractDocEmailsButton')?.addEventListener('click', () => {
  const state = contractBuilderState;
  if (!state || !state.customerId || !state.boatId) return;
  const name = [state.customer?.First_Name, state.customer?.Last_Name].filter(Boolean).join(' ');
  openEmailHistory({ recordId: `${state.customerId}_${state.boatId}` }, {
    title: `Storage contract${state.boat?.Boat_Name ? ` · ${state.boat.Boat_Name}` : ''}`,
    sub: [name, state.customer?.Email].filter(Boolean).join(' · '),
  });
});

// Email the contract as a PDF from the company account (Settings › Email).
document.getElementById('contractDocEmailButton')?.addEventListener('click', async () => {
  const state = contractBuilderState;
  if (!state) return;
  const { customer, boat, customerId, boatId } = state;
  const number = document.getElementById('contractDocNumber')?.textContent || '';
  const label = `Storage contract ${number}`.trim();
  const boatName = boat?.Boat_Name || '';
  const season = shell.storageContractSettings?.season || currentSeasonLabel();
  const pdf = pagesToPdf([...document.querySelectorAll('#contractDocContainer1 .estDoc-page')], `${label}.pdf`);
  const returnBy = shell.storageContractSettings?.dates?.returnBy;
  // A link to sign online without signing in (functions/signLinks.js, roadmap 4.1): the lines the
  // customer sees are the ones on this printed contract; the server checks the total. The lines are
  // read now (what's on screen), and the link is made only when the email goes out.
  let signLink = null;
  if (boat?.Storage_Contract && !boat.Winter_Contract_Signed) {
    const cents = t => Math.round(Number(String(t || '').replace(/[^0-9.-]/g, '')) * 100);
    const rows = sel => [...document.querySelectorAll(`#contractDocContainer1 ${sel}`)];
    const lines = [
      ...rows('.contractDoc-service-row.selected').map(r => ({ label: r.querySelector('.contractDoc-service-name')?.textContent.trim(), cents: /\$/.test(r.querySelector('.contractDoc-service-price')?.textContent || '') ? cents(r.querySelector('.contractDoc-service-price').textContent) : null })),
      ...rows('.contractDoc-totals-row:not(.contractDoc-totals-row-total)').map(r => ({ label: r.children[0]?.textContent.trim(), cents: cents(r.children[1]?.textContent) })),
      ...rows('.contractDoc-payment-row').map(r => ({ label: r.children[0]?.textContent.trim(), cents: cents(r.children[1]?.textContent) })),
    ];
    const deposit = rows('.contractDoc-payment-row')[0]?.children[1]?.textContent;
    const details = [
      { label: 'Haul week', value: state.haulWeek ? fmtEstDocDate(new Date(state.haulWeek + 'T00:00:00')) : '' },
      { label: 'Launch week', value: state.launchWeek ? fmtEstDocDate(new Date(state.launchWeek + 'T00:00:00')) : '' },
      { label: 'Zincs', value: document.getElementById('contractDocZincChoice')?.textContent.trim() || '' },
      { label: 'Requests', value: state.additionalRequests || '' },
    ];
    const snapshot = { season, lines, details, depositCents: deposit ? cents(deposit) : 0 };
    signLink = () => httpsCallable(functions, 'createSignLink', { timeout: 60000 })({ kind: 'contract', customerId, boatId, snapshot }).then(r => r.data?.url);
  }
  openCompose({
    to: String(customer?.Email || '').trim(),
    templateId: 'contract',
    vals: {
      season, ...(boatName ? { boat: boatName } : {}),
      return_by: returnBy ? fmtEstDocDate(new Date(returnBy + 'T00:00:00')) : 'the date on the contract',
    },
    links: signLink ? { sign_link: signLink } : {},
    attachments: pdf,
    customerId, docType: 'contract', docLabel: label, recordType: 'contract', recordId: boatId ? `${customerId}_${boatId}` : null,
    // Emailing a draft contract is sending it (afterSend: the same when it goes later).
    afterSend: boatId ? { kind: 'contractSent', customerId, boatId } : null,
    onSent: async () => {
      if ((state.boat?.Storage_Contract?.status || 'draft') !== 'draft' || !state.boat?.Storage_Contract) return;
      try {
        await patchStorageContract(customerId, boatId, { status: 'sent' });
        state.status = 'sent';
        renderStorageContractsList();
      } catch (e) { console.error('Could not mark the contract sent', e); }
    },
  });
});

document.getElementById('contractBuilderPrintBtn')?.addEventListener('click', () => {
  if (!contractBuilderState) {
    toastMessage('Open a storage contract first', 'red');
    return;
  }
  openContractDocument();
});

document.getElementById('contractBuilderHaulWeek')?.addEventListener('change', (e) => { if (contractBuilderState) contractBuilderState.haulWeek = e.target.value; });
document.getElementById('contractBuilderLaunchWeek')?.addEventListener('change', (e) => { if (contractBuilderState) contractBuilderState.launchWeek = e.target.value; });
document.getElementById('contractBuilderRequests')?.addEventListener('input', (e) => { if (contractBuilderState) contractBuilderState.additionalRequests = e.target.value; });
document.querySelectorAll('input[name="contractBuilderZinc"]').forEach(r => {
  r.addEventListener('change', () => { if (contractBuilderState && r.checked) contractBuilderState.zincChoice = r.value; });
});

// Shared by Save draft (below), Mark sent/completed, Sign, and Review→WO's
// completion write — one PATCH-then-mirror path for every Storage_Contract
// mutation instead of four near-duplicates.
async function patchStorageContract(customerId, boatId, patch) {
  const idToken = await fetchIdToken();
  const firebaseUrl = `${shell.currentURL}/customers/${customerId}/Boats/${boatId}/Storage_Contract.json?auth=${idToken}`;
  const response = await fetch(firebaseUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(patch) });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const db = await getDB();
  const tx = db.transaction('customerData', 'readwrite');
  const store = tx.objectStore('customerData');
  const existingCached = await new Promise((resolve) => {
    const req = store.get(customerId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
  if (existingCached?.Boats?.[boatId]) {
    existingCached.Boats[boatId].Storage_Contract = { ...(existingCached.Boats[boatId].Storage_Contract || {}), ...patch };
    store.put(existingCached);
  }
}

// Patches an arbitrary boat-record field via fetch + IndexedDB mirror —
// same shape as customers.js's saveUpdatedDataBoat, reimplemented here
// since this module doesn't import that one.
async function patchBoatField(customerId, boatId, key, value) {
  const idToken = await fetchIdToken();
  const firebaseUrl = `${shell.currentURL}/customers/${customerId}/Boats/${boatId}.json?auth=${idToken}`;
  const response = await fetch(firebaseUrl, { method: 'PATCH', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify({ [key]: value }) });
  if (!response.ok) throw new Error(`HTTP error! status: ${response.status}`);

  const db = await getDB();
  const tx = db.transaction('customerData', 'readwrite');
  const store = tx.objectStore('customerData');
  const existingCached = await new Promise((resolve) => {
    const req = store.get(customerId);
    req.onsuccess = () => resolve(req.result || null);
    req.onerror = () => resolve(null);
  });
  if (existingCached?.Boats?.[boatId]) {
    existingCached.Boats[boatId][key] = value;
    store.put(existingCached);
  }
}

document.getElementById('contractBuilderSaveBtn')?.addEventListener('click', async () => {
  if (!contractBuilderState) return;
  if (!contractBuilderState.zincChoice) {
    toastMessage('Choose a zinc replacement option before saving', 'red');
    return;
  }

  const { customerId, boatId } = contractBuilderState;
  const record = {
    season: shell.storageContractSettings?.season || '',
    status: contractBuilderState.status === 'draft' ? 'draft' : contractBuilderState.status,
    haulWeek: contractBuilderState.haulWeek,
    launchWeek: contractBuilderState.launchWeek,
    zincChoice: contractBuilderState.zincChoice,
    additionalRequests: contractBuilderState.additionalRequests,
    selections: contractBuilderState.selections,
    totals: contractBuilderState.totals,
    updatedAt: await shell.getTodaysDate(),
  };

  try {
    await patchStorageContract(customerId, boatId, record);
    toastMessage('Storage contract draft saved', 'green');
    closeStorageContractBuilderModal();
    if (typeof renderStorageContractsList === 'function' && document.getElementById('contracts-view')?.style.display !== 'none') {
      renderStorageContractsList();
    }
  } catch (error) {
    console.error('Error saving storage contract draft:', error);
    toastMessage('Error saving storage contract draft', 'red');
  }
});

// ============================================================
// H2/H3 — Storage Contracts list + Review → WO (Phase 4)
// ============================================================

async function scanStorageContracts() {
  const customers = await idbCursorAll('customerData');
  const rows = [];
  customers.forEach(customer => {
    if (!customer || !customer.Boats) return;
    Object.entries(customer.Boats).forEach(([boatId, boat]) => {
      if (!boat || !boat.Storage_Contract) return;
      rows.push({
        customer,
        boat,
        boatId,
        customerId: customer.Customer_ID || customer.id,
        contract: boat.Storage_Contract,
      });
    });
  });
  return rows;
}

// Winter_Contract_Signed is the pre-existing flag (customers.js,
// dashboard.js) — kept as the one source of truth for "signed" rather than
// introducing a second signed concept on Storage_Contract.status.
function contractDisplayStatus(row) {
  if (row.boat.Winter_Contract_Signed) return 'signed';
  return row.contract.status || 'draft';
}

// Driven by the "Unsigned only" toggle in the list header, and switched on
// automatically by the Dashboard's winter-contracts click-through (see the
// `from === 'dashboard'` message handler) so that link actually lands on the
// unsigned boats instead of the full mixed list.
let contractsUnsignedOnly = false;
// The Dashboard's "unsigned contracts" link (app.js) turns the filter on.
export function setContractsUnsignedOnly(on) { contractsUnsignedOnly = !!on; }

export async function renderStorageContractsList() {
  const listEl = document.getElementById('contracts-list');
  if (!listEl) return;

  const rows = await scanStorageContracts();
  const searchQuery = (document.getElementById('contracts-search')?.value || '').trim().toLowerCase();

  const filtered = rows.filter(row => {
    if (contractsUnsignedOnly && contractDisplayStatus(row) === 'signed') return false;
    if (!searchQuery) return true;
    const name = `${row.customer.Last_Name || ''} ${row.customer.First_Name || ''} ${row.boat.Boat_Name || ''}`.toLowerCase();
    return name.includes(searchQuery);
  });

  const unsignedCount = rows.filter(r => contractDisplayStatus(r) !== 'signed').length;
  const pill = document.getElementById('contracts-unsigned-pill');
  if (pill) pill.textContent = `${unsignedCount} unsigned`;

  // Invoice buttons: admin/office on an app-billing marina; which parts already have an invoice.
  const role = String(localStorage.getItem('currentUserRole') || '');
  const canInvoice = !usesDockmaster() && /admin|office/i.test(role);
  let contractInvoices = {};
  if (canInvoice) {
    try { contractInvoices = (await get(ref(database, 'invoiceIndex/contracts'))).val() || {}; } catch { /* no access */ }
  }

  listEl.innerHTML = '';

  if (!filtered.length) {
    listEl.innerHTML = '<div class="contracts-empty">No storage contracts yet — start one from a Storage Contract work order\'s G-rail ("Storage contract →").</div>';
    return;
  }

  filtered.forEach(row => {
    const status = contractDisplayStatus(row);
    const customerName = row.customer.Last_Name && row.customer.First_Name
      ? `${row.customer.Last_Name}, ${row.customer.First_Name}`
      : (row.customer.Last_Name || row.customer.First_Name || '');

    const rowEl = document.createElement('div');
    rowEl.className = 'contracts-row';

    const main = document.createElement('div');
    main.className = 'contracts-row-main';
    main.innerHTML = `
      <div class="contracts-row-boat">${escapeHTML(row.boat.Boat_Name || 'Boat')}</div>
      <div class="contracts-row-owner">${escapeHTML(customerName)}</div>
    `;

    const total = document.createElement('div');
    total.className = 'contracts-row-total';
    total.textContent = contractMoney(row.contract.totals?.total || 0);

    const statusPill = document.createElement('span');
    statusPill.className = 'wo-status-pill wo-status-pill-' + status;
    statusPill.textContent = status.charAt(0).toUpperCase() + status.slice(1);

    const actions = document.createElement('div');
    actions.className = 'contracts-row-actions';

    const editBtn = document.createElement('button');
    editBtn.type = 'button';
    editBtn.className = 'contracts-row-action-btn';
    editBtn.textContent = 'Edit';
    editBtn.addEventListener('click', () => openStorageContractBuilder(row.customerId, row.boatId));
    actions.appendChild(editBtn);

    if (status === 'draft' || status === 'sent') {
      const advanceBtn = document.createElement('button');
      advanceBtn.type = 'button';
      advanceBtn.className = 'contracts-row-action-btn';
      advanceBtn.textContent = status === 'draft' ? 'Mark sent' : 'Mark completed';
      advanceBtn.addEventListener('click', () => advanceContractStatus(row));
      actions.appendChild(advanceBtn);
    }

    if (!row.contract.woRefs || !row.contract.woRefs.length) {
      const reviewBtn = document.createElement('button');
      reviewBtn.type = 'button';
      reviewBtn.className = 'contracts-row-action-btn contracts-row-action-primary';
      reviewBtn.textContent = 'Review & create WOs';
      reviewBtn.addEventListener('click', () => openContractReview(row));
      actions.appendChild(reviewBtn);
    } else {
      const viewBtn = document.createElement('button');
      viewBtn.type = 'button';
      viewBtn.className = 'contracts-row-action-btn';
      viewBtn.textContent = `View WOs (${row.contract.woRefs.length})`;
      viewBtn.addEventListener('click', () => toastMessage('Work orders: ' + row.contract.woRefs.join(', '), 'green'));
      actions.appendChild(viewBtn);
    }

    // The app's own billing (admin/office): one invoice for the deposit, one for the balance.
    if (canInvoice) {
      const season = row.contract.season || shell.storageContractSettings?.season || currentSeasonLabel();
      [['deposit', 'Invoice deposit', 'Deposit'], ['balance', 'Invoice balance', 'Balance']].forEach(([part, label, short]) => {
        const invId = contractInvoices[contractIndexKey(row.customerId, row.boatId, season, part)];
        const b = document.createElement('button');
        b.type = 'button';
        b.className = 'contracts-row-action-btn app-billing-only';
        b.textContent = invId ? `${short} invoice` : label;
        b.title = invId ? 'Open the invoice' : `Make a draft ${part} invoice for this agreement`;
        b.addEventListener('click', async () => {
          if (invId) {
            const inv = (await get(ref(database, `invoices/${invId}`))).val();
            if (inv) openInvoiceEditor({ invoice: inv });
          } else {
            await invoiceStorageContract(row, part);
          }
        });
        actions.appendChild(b);
      });
    }

    if (!row.boat.Winter_Contract_Signed) {
      const signBtn = document.createElement('button');
      signBtn.type = 'button';
      signBtn.className = 'contracts-row-action-btn';
      signBtn.textContent = 'Sign';
      signBtn.title = 'Staff-confirmed signature — this app has no e-signature vendor yet';
      signBtn.addEventListener('click', () => signStorageContract(row));
      actions.appendChild(signBtn);
    }

    rowEl.appendChild(main);
    rowEl.appendChild(total);
    rowEl.appendChild(statusPill);
    rowEl.appendChild(actions);
    listEl.appendChild(rowEl);
  });
}

document.getElementById('contracts-search')?.addEventListener('input', () => renderStorageContractsList());
// Invoice deposit / balance buttons follow invoices made, voided or deleted anywhere.
window.addEventListener('invoices:changed', () => {
  if (document.getElementById('contracts-list')?.offsetParent) renderStorageContractsList();
});

document.getElementById('contracts-unsigned-toggle')?.addEventListener('click', (e) => {
  contractsUnsignedOnly = !contractsUnsignedOnly;
  e.target.classList.toggle('active', contractsUnsignedOnly);
  renderStorageContractsList();
});

// Draft -> Sent -> Completed is a manual staff toggle, not driven by a real
// digital send/complete pipeline (deferred per the staff-mediated MVP
// decision) — this button is honest about being a manual marker, not a
// simulation of a customer action.
async function advanceContractStatus(row) {
  const next = row.contract.status === 'sent' ? 'completed' : 'sent';
  try {
    await patchStorageContract(row.customerId, row.boatId, { status: next });
    toastMessage(`Marked ${next}`, 'green');
    renderStorageContractsList();
  } catch (error) {
    console.error('Error advancing contract status:', error);
    toastMessage('Error updating contract status', 'red');
  }
}

async function signStorageContract(row) {
  try {
    await patchBoatField(row.customerId, row.boatId, 'Winter_Contract_Signed', true);
    toastMessage('Marked signed', 'green');
    renderStorageContractsList();
  } catch (error) {
    console.error('Error marking contract signed:', error);
    toastMessage('Error marking contract signed', 'red');
  }
}

// "Send reminders to unsigned": the Signing log (signLog.js, roadmap 4.2) on the storage contracts
// still waiting to be signed by link, all ticked, so one click emails each their reminder.
document.getElementById('contracts-remind-btn')?.addEventListener('click', () => openSignLog({ kind: 'contract', state: 'open', pickAll: true }));

function contractMappingFor(serviceName) {
  const mappings = shell.storageContractSettings?.mappings;
  const list = (mappings && mappings.length) ? mappings : STORAGE_SERVICE_CATALOG;
  return list.find(m => m.service === serviceName) || STORAGE_SERVICE_CATALOG.find(m => m.service === serviceName);
}

let contractReviewState = null;

function openContractReviewModal() {
  const modal = document.getElementById('contractReviewModal');
  if (!modal) return;
  modal.classList.remove('hidden');
  modal.style.display = 'flex';
}

function closeContractReviewModal() {
  const modal = document.getElementById('contractReviewModal');
  if (!modal) return;
  modal.style.display = 'none';
  modal.classList.add('hidden');
}

document.getElementById('contractReviewClose')?.addEventListener('click', closeContractReviewModal);
document.getElementById('contractReviewCancelBtn')?.addEventListener('click', closeContractReviewModal);
document.getElementById('contractReviewModal')?.addEventListener('click', (e) => {
  if (e.target.id === 'contractReviewModal') closeContractReviewModal();
});

const CONTRACT_REVIEW_INCLUDED = {
  winter: [{ service: 'Haul & block for storage', opCode: 'WS01' }, { service: 'Pressure wash bottom', opCode: 'WS02' }],
  spring: [{ service: 'Launch boat', opCode: 'SP37' }, { service: 'Bottom paint, 1 coat', opCode: 'SP19' }],
  shrinkwrap: [],
};

function openContractReview(row) {
  contractReviewState = row;

  const selectedEntries = STORAGE_SERVICE_CATALOG
    .filter(entry => row.contract.selections?.[entry.service])
    .map(entry => {
      const mapping = contractMappingFor(entry.service) || entry;
      return { ...entry, opCode: mapping.opCode || '', mappedSeason: mapping.season || entry.season };
    });

  const groups = { winter: [], spring: [], shrinkwrap: [] };
  selectedEntries.forEach(entry => {
    (groups[entry.mappedSeason] || groups.winter).push(entry);
  });

  const groupsEl = document.getElementById('contractReviewGroups');
  groupsEl.innerHTML = '';
  const seasonLabels = { winter: 'Winterizing WO', spring: 'Spring commissioning WO', shrinkwrap: 'Shrinkwrap WO' };

  ['winter', 'spring', 'shrinkwrap'].forEach(season => {
    const entries = groups[season];
    if (season === 'shrinkwrap' && !entries.length) return; // dormant until used, matching G3

    const card = document.createElement('div');
    card.className = 'contract-review-card' + (season === 'shrinkwrap' ? ' contract-review-card-shrinkwrap' : '');

    let rowsHtml = CONTRACT_REVIEW_INCLUDED[season].map(i => `
      <div class="contract-review-row">
        <span class="contract-review-tag included">included</span>
        <span class="contract-review-name">${escapeHTML(i.service)}</span>
        <span class="contract-review-code">${i.opCode}</span>
        <span class="contract-review-price">incl.</span>
      </div>
    `).join('');

    rowsHtml += entries.map(e => `
      <div class="contract-review-row">
        <span class="contract-review-tag ${e.opCode ? 'contract' : 'unmapped'}">${e.opCode ? 'contract' : 'unmapped'}</span>
        <span class="contract-review-name">${escapeHTML(e.service)}</span>
        <span class="contract-review-code">${e.opCode || '—'}</span>
        <span class="contract-review-price">${e.unit === 'tm' ? 'T&M' : contractMoney(contractBuilderServicePrice(e, row.boat))}</span>
      </div>
    `).join('');

    card.innerHTML = `
      <div class="contract-review-card-head">
        <span>${seasonLabels[season]}</span>
        <span class="wo-status-pill wo-status-pill-sent">will generate</span>
      </div>
      ${rowsHtml || '<div class="contract-review-empty">Nothing selected for this season.</div>'}
    `;
    groupsEl.appendChild(card);
  });

  const summary = document.getElementById('contractReviewSummary');
  if (summary) {
    const customerName = row.customer.Last_Name && row.customer.First_Name ? `${row.customer.Last_Name}, ${row.customer.First_Name}` : '';
    summary.innerHTML = `<strong>${escapeHTML(row.boat.Boat_Name || 'Boat')}</strong> — ${escapeHTML(customerName)} &middot; ${contractMoney(row.contract.totals?.total || 0)}`;
  }

  openContractReviewModal();
}

// Populates the op-detail form's hidden fields the same way a human would
// via the op picker, then reuses the existing, already-tested addJob()
// pipeline (Dockmaster creation + IndexedDB mirror + Operations row) — no
// parallel "create an op" implementation to keep in sync.
async function addContractServiceOp(entry, mapping, workOrderNumber) {
  document.getElementById('opCode').value = mapping.opCode || '';
  document.getElementById('opType').value = '';
  document.getElementById('dmShortDesc').value = entry.service;
  document.getElementById('description').value = entry.service;
  document.getElementById('category').value = (mapping.season || entry.season) === 'spring' ? 'Spring Commissioning' : 'Winterizing';
  document.getElementById('estimatedHours').value = '';
  document.getElementById('estimatedCharge').value = entry.unit === 'tm' ? '' : contractBuilderServicePrice(entry, contractReviewState?.boat);
  document.getElementById('status').value = 'On Work Schedule';
  document.getElementById('begin').value = '';
  const dateEl = document.getElementById('date');
  if (dateEl) dateEl.value = '';
  await shell.addJob(undefined, workOrderNumber);
}

// H3b — progress UI for the "Create storage contract + WOs" button. This
// flow chains several sequential RTDB round-trips (2-3 WO-number
// transactions + one addJob() write per contract service), easily a few
// seconds end to end, so it gets a real step-by-step readout instead of
// the per-operation "Work Order Added" toasts (suppressed for the duration
// via suppressWorkOrderAddedToast — they'd otherwise fire once per op and
// just stomp on each other in the single shared snackbar).
export let suppressWorkOrderAddedToast = false;
let contractReviewCreating = false;

function updateContractReviewProgress(label, step, total) {
  const wrap = document.getElementById('contractReviewProgress');
  if (!wrap) return;
  wrap.classList.remove('hidden');
  const fill = document.getElementById('contractReviewProgressFill');
  const text = document.getElementById('contractReviewProgressLabel');
  const pct = total > 0 ? Math.min(100, Math.round((step / total) * 100)) : 0;
  if (fill) fill.style.width = pct + '%';
  if (text) text.textContent = label;
}

function hideContractReviewProgress() {
  document.getElementById('contractReviewProgress')?.classList.add('hidden');
}

document.getElementById('contractReviewCreateBtn')?.addEventListener('click', async () => {
  if (!contractReviewState || contractReviewCreating) return;
  const row = contractReviewState;
  const { customer, boat, customerId, boatId } = row;

  const createBtn = document.getElementById('contractReviewCreateBtn');
  const cancelBtn = document.getElementById('contractReviewCancelBtn');
  contractReviewCreating = true;
  suppressWorkOrderAddedToast = true;
  createBtn.disabled = true;
  if (cancelBtn) cancelBtn.disabled = true;

  try {
    // These WO-number fields are shared with the general "+Add Work Order"
    // modal and the "add op to an existing WO" flow, both of which can
    // leave a leftover number sitting in them from a previous, unrelated
    // work order. Always start this run from a clean slate — never trust a
    // value already here — and from this point on pass the numbers this
    // run mints around as local variables instead of re-reading these
    // shared fields, so nothing else touching them mid-flight can split
    // this contract's operations across two work orders.
    document.getElementById('winterWorkOrder').value = '';
    document.getElementById('springWorkOrder').value = '';
    document.getElementById('shrinkwrapWorkOrder').value = '';

    // Populates the WO creation form's hidden customer/boat fields "headless"
    // (the #Add modal itself never opens) so getWorkOrderNumber()/addJob()
    // — the exact same pipeline the G-rail already uses — can run unchanged.
    shell.selectCustomerAndBoat(customer, boat);
    document.getElementById('work-order-type-select').value = 'storage-work-order';
    document.getElementById('work-order-form-storage-confirm').checked = true;

    const selectedEntries = STORAGE_SERVICE_CATALOG.filter(entry => row.contract.selections?.[entry.service]);
    const mappedEntries = selectedEntries.filter(entry => (contractMappingFor(entry.service) || entry).opCode);
    // Only open a Shrinkwrap WO if a shrinkwrap service will actually become an operation: the
    // catalog's shrink-wrap rows have no op code until one is mapped (they show as "unmapped" in
    // review), and counting them anyway created an empty WO on every such contract.
    const needsShrinkwrap = mappedEntries.some(entry => (contractMappingFor(entry.service) || entry).season === 'shrinkwrap');

    const totalSteps = 2 + (needsShrinkwrap ? 1 : 0) + mappedEntries.length + 1;
    let step = 0;

    updateContractReviewProgress(`Assigning Winterizing WO number… (${++step}/${totalSteps})`, step, totalSteps);
    const winterWo = await shell.getWorkOrderNumber('winter');

    updateContractReviewProgress(`Assigning Spring Commissioning WO number… (${++step}/${totalSteps})`, step, totalSteps);
    const springWo = await shell.getWorkOrderNumber('spring');

    let shrinkwrapWo = '';
    if (needsShrinkwrap) {
      updateContractReviewProgress(`Assigning Shrinkwrap WO number… (${++step}/${totalSteps})`, step, totalSteps);
      shrinkwrapWo = await shell.getWorkOrderNumber('shrinkwrap');
    }

    const woRefs = [winterWo, springWo, shrinkwrapWo].filter(Boolean);
    const woBySeason = { winter: winterWo, spring: springWo, shrinkwrap: shrinkwrapWo };

    for (const entry of mappedEntries) {
      const mapping = contractMappingFor(entry.service) || entry;
      const season = mapping.season || entry.season;
      const workOrderNumber = woBySeason[season];
      if (!workOrderNumber) continue;
      updateContractReviewProgress(`Adding ${entry.service}… (${++step}/${totalSteps})`, step, totalSteps);
      await addContractServiceOp(entry, mapping, workOrderNumber);
    }

    updateContractReviewProgress(`Saving contract… (${++step}/${totalSteps})`, step, totalSteps);
    await patchStorageContract(customerId, boatId, { status: 'completed', woRefs });

    toastMessage('Storage contract work orders created', 'green');
    closeContractReviewModal();
    renderStorageContractsList();
  } catch (error) {
    console.error('Error creating storage contract work orders:', error);
    toastMessage('Error creating work orders', 'red');
  } finally {
    contractReviewCreating = false;
    suppressWorkOrderAddedToast = false;
    createBtn.disabled = false;
    if (cancelBtn) cancelBtn.disabled = false;
    hideContractReviewProgress();
  }
});
